import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth, hasMinRole } from "@/context/AuthContext";
import { PageHeader, Card, Loading, EmptyState } from "@/components/common";
import { MEETING_STATUS, MEETING_TYPE_LABELS, fmtMeetingDate } from "@/lib/meetingsApi";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, Plus, MapPin, ArrowRight, Check, ChevronRight, ChevronLeft } from "lucide-react";

function StatusBadge({ status }) {
  const s = MEETING_STATUS[status] || { label: status };
  return <span data-testid="meeting-status-badge" className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.cls}`}>{s.label}</span>;
}

export default function Meetings() {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState(null);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const isClerk = hasMinRole(user?.role, "manager");

  const load = () => {
    setMeetings(null);
    api.get("/meetings?limit=100").then((r) => setMeetings(r.data.meetings)).catch(() => setError("Unable to load meetings."));
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="meetings-page">
      <PageHeader title="Meetings" subtitle="Governing-body meetings, agendas and outcomes."
        actions={isClerk && (
          <button data-testid="create-meeting-btn" onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900">
            <Plus className="h-4 w-4" /> Create Meeting
          </button>
        )} />
      {error ? <EmptyState icon={CalendarDays} title="Unable to load" description={error} />
        : !meetings ? <Loading label="Loading meetings…" />
        : meetings.length === 0
          ? <EmptyState icon={CalendarDays} title="No meetings yet"
              description={isClerk ? "Create a meeting to start building its agenda." : "Scheduled meetings will appear here."} />
          : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {meetings.map((m) => (
                <Link key={m.meeting_id} to={`/meetings/${m.meeting_id}`} data-testid="meeting-card"
                  className="rounded-xl border border-brand-line bg-white p-4 shadow-card hover:border-brand-civic/40 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-brand-civic">{m.governing_body_name}</span>
                    <StatusBadge status={m.status} />
                  </div>
                  <h3 className="font-display font-bold text-brand-ink mt-1.5">{m.title}</h3>
                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> {fmtMeetingDate(m.date, m.start_time)}</p>
                  {(m.location || m.virtual_url) && <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {m.location || m.virtual_url}</p>}
                  <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-brand-navy">
                    {MEETING_TYPE_LABELS[m.meeting_type] || m.meeting_type} <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              ))}
            </div>
          )}
      {showCreate && <CreateMeetingDialog onClose={() => setShowCreate(false)} onCreated={(id) => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CreateMeetingDialog({ onClose, onCreated }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    governing_body_id: "", meeting_type: "regular", title: "", date: "", start_time: "18:00",
    location: "", virtual_url: "",
  });

  useEffect(() => {
    api.get("/meetings/config").then((r) => {
      setCfg(r.data);
      if (r.data.governing_bodies.length === 1) setF((s) => ({ ...s, governing_body_id: r.data.governing_bodies[0].body_id }));
    }).catch(() => setCfg({ governing_bodies: [], meeting_types: [] }));
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const bodyName = cfg?.governing_bodies.find((b) => b.body_id === f.governing_body_id)?.name || "";
  const canNext = () => (step === 0 ? f.governing_body_id && f.meeting_type : step === 1 ? f.date && (f.location || f.virtual_url) : true);

  useEffect(() => {
    if (step === 1 && !f.title && bodyName) set("title", `${bodyName} ${cfg.meeting_types.includes(f.meeting_type) ? f.meeting_type.replace("_", " ") : "meeting"}`.replace(/\b\w/g, (c) => c.toUpperCase()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/meetings", f);
      toast.success("Meeting created");
      onCreated(data.meeting_id);
      navigate(`/meetings/${data.meeting_id}/agenda`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="create-meeting-dialog">
        <DialogHeader><DialogTitle>Create Meeting — {["What kind?", "When & where?", "Confirm"][step]}</DialogTitle></DialogHeader>
        {!cfg ? <Loading label="Loading…" /> : (
          <div className="space-y-3">
            {step === 0 && (
              <>
                <L label="Governing Body">
                  <select data-testid="meeting-body" value={f.governing_body_id} onChange={(e) => set("governing_body_id", e.target.value)} className={inp}>
                    <option value="">{`Select…`}</option>
                    {cfg.governing_bodies.map((b) => <option key={b.body_id} value={b.body_id}>{b.name}</option>)}
                  </select>
                </L>
                <L label="Meeting Type">
                  <select data-testid="meeting-type" value={f.meeting_type} onChange={(e) => set("meeting_type", e.target.value)} className={inp}>
                    {cfg.meeting_types.map((t) => <option key={t} value={t}>{MEETING_TYPE_LABELS[t] || t}</option>)}
                  </select>
                </L>
              </>
            )}
            {step === 1 && (
              <>
                <L label="Title"><input data-testid="meeting-title" value={f.title} onChange={(e) => set("title", e.target.value)} className={inp} /></L>
                <div className="grid grid-cols-2 gap-3">
                  <L label="Date"><input data-testid="meeting-date" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className={inp} /></L>
                  <L label="Start Time"><input data-testid="meeting-time" type="time" value={f.start_time} onChange={(e) => set("start_time", e.target.value)} className={inp} /></L>
                </div>
                <L label="Location"><input data-testid="meeting-location" value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g., City Hall Chambers" className={inp} /></L>
                <L label="Virtual URL (optional)"><input data-testid="meeting-url" value={f.virtual_url} onChange={(e) => set("virtual_url", e.target.value)} className={inp} /></L>
                <p className="text-xs text-slate-400">A standard agenda template will be applied automatically.</p>
              </>
            )}
            {step === 2 && (
              <div className="text-sm space-y-1.5" data-testid="meeting-confirm">
                <p><strong>{bodyName}</strong> · {MEETING_TYPE_LABELS[f.meeting_type]}</p>
                <p>{f.title}</p>
                <p>{fmtMeetingDate(f.date, f.start_time)}</p>
                <p className="text-slate-500">{f.location || f.virtual_url}</p>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {step > 0 && <button onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-1 rounded-lg border border-brand-line px-3 py-2 text-sm font-medium text-slate-600 hover:bg-brand-bg"><ChevronLeft className="h-4 w-4" /> Back</button>}
          {step < 2
            ? <button data-testid="meeting-next" disabled={!canNext()} onClick={() => setStep((s) => s + 1)} className="inline-flex items-center gap-1 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900 disabled:opacity-40">Next <ChevronRight className="h-4 w-4" /></button>
            : <button data-testid="meeting-create-confirm" disabled={saving} onClick={create} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900 disabled:opacity-40"><Check className="h-4 w-4" /> Create & Build Agenda</button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const inp = "w-full h-10 rounded-lg border border-brand-line bg-white px-3 text-sm outline-none focus:border-brand-civic";
function L({ label, children }) {
  return <div><label className="text-sm font-semibold text-brand-ink mb-1 block">{label}</label>{children}</div>;
}
